import {
  FREE_NOTE_TOPICS,
  REGISTER_NOTE_MAX,
  REGISTER_NOTE_MAX_TOPICS,
  REGISTER_NOTE_TOPIC_LABELS,
  SALE_NOTE_TOPICS,
  registerNoteTopicsText,
  type RegisterNoteTopic,
} from '@barso/shared';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Button, TextField } from '../components/ui';
import type { PosStore } from '../pos/store';
import type { RegisterNoteEntry } from '../pos/types';
import { Sheet } from './posParts';
import '../components/reasonChips.css';

/** Worauf sich der Kommentar bezieht: auf einen Verkauf (und ggf. eine Position) oder auf nichts („Problem melden“). */
export interface NoteTarget {
  saleClientUuid?: string | undefined;
  lineNo?: number | undefined;
  /** Kurzbezeichnung für die Überschrift, z. B. „Verkauf Nr. 41“. */
  label?: string | undefined;
}

/**
 * Blatt „Etwas dazu sagen“ bzw. „Problem melden“ (U17f): Schnellwahl (mehrere Themen möglich, höchstens vier), dazu ein freier
 * Text bis 300 Zeichen. Kein Zwangsdialog: Das Blatt öffnet nur auf Wunsch. „Senden“ speichert den Kommentar auf dem Gerät,
 * er geht mit dem nächsten Abgleich hinaus, auch ohne Netz.
 */
export function NoteSheet({
  target,
  messagesHref,
  onSend,
  onClose,
}: {
  target: NoteTarget;
  /** Nur mit Konto und Netz: Link zum Gespräch mit der Basar-Leitung (der Schnellzugang hat keine Nachrichten). */
  messagesHref?: string | undefined;
  onSend: (topics: RegisterNoteTopic[], text: string | undefined) => void;
  onClose: () => void;
}) {
  const topicsList = target.saleClientUuid === undefined ? FREE_NOTE_TOPICS : SALE_NOTE_TOPICS;
  const [topics, setTopics] = useState<RegisterNoteTopic[]>([]);
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const ready = topics.length > 0 || trimmed !== '';
  const title = target.label === undefined ? 'Problem melden' : `Etwas zu ${target.label} sagen`;
  return (
    <Sheet title={title} dismissible={false} onClose={onClose}>
      <div className="reason">
        <span className="reason__label" id="note-topics">
          Was ist passiert?
        </span>
        <div className="reason__chips" role="group" aria-labelledby="note-topics">
          {topicsList.map((topic) => {
            const on = topics.includes(topic);
            return (
              <button
                key={topic}
                type="button"
                className="chip"
                aria-pressed={on}
                disabled={!on && topics.length >= REGISTER_NOTE_MAX_TOPICS}
                onClick={() => {
                  setTopics((current) =>
                    on ? current.filter((entry) => entry !== topic) : [...current, topic],
                  );
                }}
              >
                {REGISTER_NOTE_TOPIC_LABELS[topic]}
              </button>
            );
          })}
        </div>
      </div>
      <TextField
        label="Text (freiwillig)"
        hint="Bitte keine Namen oder Kontaktdaten."
        value={text}
        maxLength={REGISTER_NOTE_MAX}
        autoComplete="off"
        onChange={(event) => {
          setText(event.target.value);
        }}
      />
      <span className="reason__counter" aria-hidden="true">
        {`${String(Array.from(text).length)}/${String(REGISTER_NOTE_MAX)}`}
      </span>
      <p className="muted">Wird mit dem nächsten Abgleich gesendet, auch ohne Netz gespeichert.</p>
      <div className="row">
        <Button
          disabled={!ready}
          onClick={() => {
            onSend(topics, trimmed === '' ? undefined : trimmed);
          }}
        >
          Senden
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Abbrechen
        </Button>
      </div>
      {messagesHref !== undefined && (
        <Link to={messagesHref}>Gespräch mit der Basar-Leitung öffnen</Link>
      )}
    </Sheet>
  );
}

/** Zeile „Problem gemeldet ✓“ bzw. „… (wartet auf Netz)“ samt eigenem Text, wenn zu diesem Verkauf ein Kommentar vorliegt. */
export function noteStatusLines(notes: readonly RegisterNoteEntry[]): string[] {
  return notes.map((note) => {
    const state =
      note.sentAtMs !== undefined
        ? 'Problem gemeldet ✓'
        : note.status === 'failed'
          ? 'Problem nicht zustellbar'
          : 'Problem gemeldet (wartet auf Netz)';
    const topics = registerNoteTopicsText(note.topics);
    const text = note.text === undefined ? '' : `„${note.text}“`;
    return [state, [topics, text].filter((part) => part !== '').join(' · ')]
      .filter((part) => part !== '')
      .join(': ');
  });
}

/** Zeigt die Kommentare zu einem Verkauf; liest den Speicher bei jeder Änderung von `version` neu. */
export function SaleNoteStatus({
  store,
  saleClientUuid,
  version,
}: {
  store: PosStore;
  saleClientUuid: string;
  version: number;
}) {
  const [lines, setLines] = useState<string[]>([]);
  useEffect(() => {
    const guard = { cancelled: false };
    void store.notes().then((all) => {
      if (!guard.cancelled) {
        setLines(noteStatusLines(all.filter((note) => note.saleClientUuid === saleClientUuid)));
      }
    });
    return () => {
      guard.cancelled = true;
    };
  }, [store, saleClientUuid, version]);
  if (lines.length === 0) return null;
  return (
    <>
      {lines.map((line) => (
        <span key={line} className="muted">
          {line}
        </span>
      ))}
    </>
  );
}
